import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { Layout } from "../components/Layout";
import { Reveal } from "../components/Reveal";
import { CtaSection } from "../components/CtaSection";
import { usePageMeta } from "../hooks/usePageMeta";
import { fileUrl } from "../lib/files";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch (e) {
    return "";
  }
};

const InsightsPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    axios
      .get(`${API}/posts/${slug}`)
      .then(({ data }) => {
        setPost(data);
        setStatus("ready");
      })
      .catch((err) => {
        setStatus(err?.response?.status === 404 ? "not_found" : "error");
      });
  }, [slug]);

  usePageMeta(
    post ? `${post.title} | Elin's Consultancy` : "Insights | Elin's Consultancy",
    post?.excerpt || "Plain‑English takes on HMO licensing, Stamp Duty and leasehold from Elin's Consultancy."
  );

  // Article JSON-LD for SEO / AI citation
  useEffect(() => {
    if (!post) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt || undefined,
      author: { "@type": "Organization", name: post.author || "Elin's Consultancy" },
      publisher: {
        "@type": "Organization",
        name: "Elin's Consultancy",
      },
      datePublished: post.published_at || post.created_at,
      dateModified: post.updated_at || post.published_at || post.created_at,
      articleSection: post.category || undefined,
      image: post.cover_image ? fileUrl(post.cover_image) : undefined,
    };
    // Remove undefined keys for a clean payload
    Object.keys(schema).forEach((k) => schema[k] === undefined && delete schema[k]);
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-testid", "insights-jsonld");
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [post]);

  if (status === "loading") {
    return (
      <Layout>
        <div className="wrap-x py-32 text-center text-sage text-sm">Loading article…</div>
      </Layout>
    );
  }

  if (status === "not_found") {
    return (
      <Layout>
        <div className="wrap-x py-32 text-center max-w-xl mx-auto">
          <p className="eyebrow text-gold-dark">404</p>
          <h1 className="font-serif text-4xl text-forest mt-5 mb-5">
            That article isn't here.
          </h1>
          <p className="text-[14.5px] leading-[1.8] text-sage">
            It may have been moved or taken down. Head back to the full list of pieces.
          </p>
          <Link to="/insights" data-testid="insights-back-404" className="btn-forest mt-7">
            <ArrowLeft size={14} strokeWidth={2.2} /> Back to Insights
          </Link>
        </div>
      </Layout>
    );
  }

  if (status === "error" || !post) {
    return (
      <Layout>
        <div className="wrap-x py-32 text-center text-sage text-sm">
          Unable to load this article — please try again shortly.
        </div>
      </Layout>
    );
  }

  const cover = post.cover_image ? fileUrl(post.cover_image) : null;

  return (
    <Layout>
      <article data-testid="insights-post" className="bg-cream">
        <header className="relative pt-32 pb-14 sm:pt-40 sm:pb-20 overflow-hidden">
          {cover && (
            <div aria-hidden="true" className="absolute inset-0 opacity-30">
              <img
                src={cover}
                alt=""
                className="w-full h-full object-cover kenburns"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-cream/60 via-cream/85 to-cream" />
            </div>
          )}
          <div className="wrap-x relative z-[2] max-w-3xl">
            <Link
              to="/insights"
              data-testid="insights-back"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-forest/70 hover:text-forest transition-colors"
            >
              <ArrowLeft size={13} strokeWidth={2} /> All Insights
            </Link>
            <div className="flex flex-wrap items-center gap-3 mt-8 mb-6">
              <span
                data-testid="insights-category"
                className="text-[10.5px] tracking-[0.28em] uppercase font-bold text-gold-dark bg-cream-3 border border-gold/40 px-3 py-1.5"
              >
                {post.category || "Insights"}
              </span>
              <span className="inline-flex items-center gap-2 text-[12px] text-sage">
                <Calendar size={13} strokeWidth={1.6} />
                {fmtDate(post.published_at || post.created_at)}
              </span>
              <span className="inline-flex items-center gap-2 text-[12px] text-sage">
                <User size={13} strokeWidth={1.6} />
                {post.author || "Elin's Consultancy"}
              </span>
            </div>
            <Reveal>
              <h1
                data-testid="insights-title"
                className="font-serif text-4xl sm:text-5xl lg:text-[56px] font-medium text-forest leading-[1.08]"
              >
                {post.title}
              </h1>
            </Reveal>
            {post.excerpt && (
              <Reveal delay={0.1}>
                <p className="mt-7 text-[16px] leading-[1.75] text-sage max-w-2xl">
                  {post.excerpt}
                </p>
              </Reveal>
            )}
          </div>
        </header>

        <section className="bg-cream">
          <div className="wrap-x pb-20 sm:pb-28">
            <div
              data-testid="insights-body"
              className="prose-elin max-w-3xl mx-auto"
            >
              <ReactMarkdown>{post.body}</ReactMarkdown>
            </div>
          </div>
        </section>
      </article>

      <CtaSection
        title={
          <>
            Thinking about a
            <br />
            <em className="italic font-normal text-gold-soft">specific purchase?</em>
          </>
        }
        text="If this piece raised a question about your situation, send us a note — we'll come back with an honest, specific answer."
      />
    </Layout>
  );
};

export default InsightsPost;
